"use client";

import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import type { FormikErrors } from "formik";
import {
	login,
	getOtp,
	getUserData,
	updateUserData,
} from "@/app/product/_actions/user.actions";
import { useAppData, useAppStore } from "@/store/appStore";
import { clientLogger } from "@/lib/logger";
import { useConfig } from "@/hooks/useConfig";
import { useRouter } from "next/navigation";
import { OTPInput } from "@/design-elements/otp-input";
import { validateOtp } from "@/lib/utils";
import { Logo } from "@/design-elements/logo";
import { syncDeviceId } from "@/lib/utils/device-id";
import { getClearedProductListFilters } from "@/lib/utils/product-list-filters";
import { trackEvent, GTMEvent, getUTMFromURL } from "@/lib/utils/gtm-events";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import EditIcon from "@/components/icons/EditIcon";
import {
	fetchConsentDocument,
	submitConsentAcceptance,
} from "@/lib/client/consent";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";

interface StepOtpProps {
	onSubmit: (nextStep?: string) => void;
	onSkip?: () => void;
	onBack?: () => void;
	initialOtp?: string;
	emailForOtp?: string;
	shouldRefreshAfterLogin?: boolean;
}

interface FormValues {
	otp: string;
	captcha: string | null;
}

const OTP_COPY = {
	heroTitle: "Verify your email",
	label: "Verification code",
	verify: "Submit",
	verifying: "Verifying...",
	resendPrompt: "Didn't receive the code?",
	resendCta: "Resend",
	resendCountdown: (seconds: number) => `Resend in ${seconds}s`,
	skip: "Skip",
	errorInvalidOtp: "Please enter a valid 5-digit OTP.",
	errorVerificationFailed: "OTP verification failed. Please try again.",
	errorUnexpected: "An unexpected error occurred.",
	errorResendFailed: "Failed to resend OTP. Please try again.",
	errorResendUnexpected: "An unexpected error occurred while resending OTP.",
	logSkip: "Onboarding: OTP step skipped",
	logSuccess: "Onboarding: OTP verified successfully",
};

const StepOtp: React.FC<StepOtpProps> = ({
	onSubmit,
	onSkip,
	initialOtp = "",
	emailForOtp,
	onBack,
	shouldRefreshAfterLogin = false,
}) => {
	const router = useRouter();
	const [appData, setAppData, setCookielessData] = useAppData();
	useConfig();
	const turnstileRef = useRef<TurnstileInstance>(null);
	const [resendTimerActive, setResendTimerActive] = useState(true);
	const [resendTimerCount, setResendTimerCount] = useState(30);
	const [lastFailedOtp, setLastFailedOtp] = useState<string | null>(null);

	const formik = useFormik<FormValues>({
		initialValues: { otp: initialOtp, captcha: null },
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			if (values.otp && !validateOtp(values.otp)) {
				errors.otp = OTP_COPY.errorInvalidOtp;
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting, setFieldError }) => {
			if (values.otp === lastFailedOtp) {
				setSubmitting(false);
				return;
			}
			if (!validateOtp(values.otp)) {
				setFieldError("otp", OTP_COPY.errorInvalidOtp);
				setSubmitting(false);
				return;
			}
			if (!values.captcha) {
				setSubmitting(false);
				return;
			}
			setFieldError("otp", undefined);

			try {
				const result = await login(values.otp, values.captcha);
				if (
					result &&
					typeof result === "object" &&
					"success" in result &&
					result.success
				) {
					if (result.data?.guestUser?.deviceId) {
						syncDeviceId(result.data.guestUser.deviceId);
						clientLogger.info("Synced device to original guest (onboarding)", {
							deviceId: result.data.guestUser.deviceId,
						});
					}

					let nextStep: string | undefined;
					try {
						const userData = await getUserData();
						if (userData) {
							const appUpdates: Record<string, unknown> = {
								email: userData.email || appData.email,
								hasAcceptedOnBoardingConsent: userData.hasAcceptedConsent,
							};

							if (userData.employment?.annualCtc) {
								appUpdates.ctc = userData.employment.annualCtc;
							} else if (appData.ctc) {
								updateUserData({ ctc: appData.ctc }).catch(() => {});
							}

							if (userData.mobileNo) {
								appUpdates.phoneNumber = userData.mobileNo;
							}

							setAppData(appUpdates);
							setCookielessData({
								user: userData,
								isUserReady: true,
								...getClearedProductListFilters(true),
							});

							const consentRequiredFlag =
								useAppStore.getState().data.isOnboardingConsentEnabled;
							if (
								consentRequiredFlag !== false &&
								!userData?.hasAcceptedConsent
							) {
								try {
									const doc = await fetchConsentDocument("onboarding");
									if (doc?.id) {
										const consentRes = await submitConsentAcceptance(doc.id);
										if (consentRes.success) {
											trackEvent(GTMEvent.CONSENT_ACCEPTED, {
												page_path: "Onboarding page",
											});
											setAppData({ hasAcceptedOnBoardingConsent: true });
										}
									}
								} catch (consentErr) {
									clientLogger.error(
										"Onboarding: Failed to persist consent post email-OTP",
										consentErr as Error,
									);
								}
							}

							const storeValue = useAppStore.getState().data.isExistingUser;
							const isExistingUser =
								storeValue !== undefined
									? storeValue
									: sessionStorage.getItem("isExistingUser") === "true";

							if (isExistingUser) {
								const consentRequired =
									useAppStore.getState().data.isOnboardingConsentEnabled;
								if (
									!userData?.firstName ||
									!userData?.lastName ||
									(!userData?.hasAcceptedConsent && consentRequired)
								) {
									nextStep = "user-info";
								} else {
									nextStep = "device-selection";
								}
							}
						}
					} catch (error) {
						clientLogger.error(
							"getUserData failed in step-otp",
							error as Error,
						);
					}

					const trackStoreValue = useAppStore.getState().data.isExistingUser;
					const isExistingUser =
						trackStoreValue !== undefined
							? trackStoreValue
							: sessionStorage.getItem("isExistingUser") === "true";
					trackEvent(isExistingUser ? GTMEvent.LOGIN : GTMEvent.SIGNUP, {
						auth_method: "otp",
						page_path: "Onboarding page",
						...getUTMFromURL(),
					});

					clientLogger.info(OTP_COPY.logSuccess, { emailForOtp });

					setLastFailedOtp(values.otp);
					onSubmit(nextStep);
					setFieldError("otp", undefined);

					if (shouldRefreshAfterLogin) {
						setTimeout(() => {
							router.refresh();
						}, 0);
					}
				} else {
					setLastFailedOtp(values.otp);
					turnstileRef.current?.reset();
					formik.setFieldValue("captcha", null, false);
					setFieldError("otp", OTP_COPY.errorVerificationFailed);
					setSubmitting(false);
				}
			} catch (err) {
				setLastFailedOtp(values.otp);
				turnstileRef.current?.reset();
				formik.setFieldValue("captcha", null, false);
				setFieldError(
					"otp",
					err instanceof Error ? err.message : OTP_COPY.errorUnexpected,
				);
				setSubmitting(false);
			}
		},
	});

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (resendTimerActive && resendTimerCount > 0) {
			interval = setInterval(() => {
				setResendTimerCount((prev) => prev - 1);
			}, 1000);
		} else if (resendTimerCount === 0) {
			setResendTimerActive(false);
		}
		return () => clearInterval(interval);
	}, [resendTimerActive, resendTimerCount]);

	useEffect(() => {
		if (
			formik.values.captcha &&
			validateOtp(formik.values.otp) &&
			!formik.isSubmitting &&
			formik.values.otp !== lastFailedOtp
		) {
			formik.setFieldTouched("otp", true, false);
			queueMicrotask(() => {
				formik.submitForm();
			});
		}
	}, [
		formik.values.captcha,
		formik.values.otp,
		formik.isSubmitting,
		lastFailedOtp,
	]);

	const handleResendCode = async () => {
		if (resendTimerActive || !emailForOtp) return;

		try {
			const response = await getOtp(emailForOtp, "email");
			if (!response?.error) {
				setResendTimerActive(true);
				setResendTimerCount(30);
				formik.setFieldError("otp", undefined);
			} else {
				formik.setFieldError(
					"otp",
					response?.errorText || OTP_COPY.errorResendFailed,
				);
			}
		} catch (err) {
			formik.setFieldError(
				"otp",
				err instanceof Error ? err.message : OTP_COPY.errorResendUnexpected,
			);
		}
	};

	const handleSkip = () => {
		// Clear any displayed error / in-flight validation before navigating away
		// so the error doesn't flash alongside the skip transition.
		formik.resetForm();
		trackEvent(GTMEvent.EMAIL_VERIFICATION_SKIPPED);
		clientLogger.info(OTP_COPY.logSkip);
		if (onSkip) {
			onSkip();
		}
	};

	const otpError = formik.touched.otp ? formik.errors.otp : undefined;
	const canSubmitOtp =
		validateOtp(formik.values.otp) &&
		!!formik.values.captcha &&
		formik.values.otp !== lastFailedOtp;
	const submitDisabled = formik.isSubmitting || !canSubmitOtp;
	const showSubmitDisabledStyle = submitDisabled && !formik.isSubmitting;

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px]  leading-tight">
							{OTP_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3">
							We&apos;ve sent a verification code to
							<br />
							your email{" "}
							{onBack ? (
								<button
									type="button"
									onClick={onBack}
									aria-label="Edit email"
									className="inline-flex items-center gap-1 align-baseline pb-0.5 border-b border-dotted text-[var(--automint-black)] border-[var(--automint-black)] md:text-white md:border-white"
								>
									<span>{emailForOtp || "your inbox"}</span>
									<EditIcon size={14} className="inline-block" />
								</button>
							) : (
								<span className="text-[var(--automint-black)] md:text-white underline decoration-dotted">
									{emailForOtp || "your inbox"}
								</span>
							)}
						</p>
					</div>

					<div className="mt-6">
						<p className="text-sm text-[var(--automint-black)] md:text-white mb-2">
							{OTP_COPY.label}
						</p>
						<OTPInput
							length={5}
							required
							value={formik.values.otp}
							onChange={(otpValue) => {
								formik.setFieldValue("otp", otpValue);
								if (!formik.touched.otp) {
									formik.setFieldTouched("otp", true, false);
								}
							}}
							onComplete={() => {}}
							errorText={otpError}
							autoFocus
							className="[&>div]:!w-full [&>div]:!flex [&>div]:!gap-2 md:[&>div]:!gap-3 [&_input]:!flex-1 [&_input]:!min-w-0 [&_input]:!w-0 [&_input]:!h-12 md:[&_input]:!h-14 [&_input]:!bg-[var(--colors-automint-neutral-200)] [&_input]:!border-[var(--colors-automint-neutral-200)] [&_input]:!text-[var(--automint-black)] md:[&_input]:!bg-[var(--colors-automint-neutral-800)] md:[&_input]:!border-[var(--colors-automint-neutral-800)] md:[&_input]:!text-white [&_input]:!rounded-[6px] [&_input]:!text-lg md:[&_input]:!text-xl"
						/>
					</div>

					{/* Turnstile always rendered (interaction-only = invisible until a
					challenge is actually needed) so it doesn't grow the card the moment
					the OTP becomes valid. */}
					<div className="mt-1">
						<Turnstile
							ref={turnstileRef}
							siteKey={SITE_KEY}
							onSuccess={(token) => formik.setFieldValue("captcha", token)}
							onError={() => formik.setFieldValue("captcha", null)}
							onExpire={() => formik.setFieldValue("captcha", null)}
							options={{
								theme: "light",
								appearance: "interaction-only",
							}}
						/>
					</div>

					<div className="mt-3 flex items-center justify-between text-sm">
						<p className="text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]">
							{OTP_COPY.resendPrompt}
						</p>
						<button
							id="onboarding_email_otp_resend"
							type="button"
							onClick={handleResendCode}
							disabled={
								formik.isSubmitting || !emailForOtp || resendTimerActive
							}
							className="text-[var(--automint-black)] md:text-white underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
						>
							{resendTimerActive
								? OTP_COPY.resendCountdown(resendTimerCount)
								: OTP_COPY.resendCta}
						</button>
					</div>

					<div className="mt-5">
						<button
							id="onboarding_email_otp_verify"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={submitDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSubmitDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting ? OTP_COPY.verifying : OTP_COPY.verify}
						</button>
					</div>

					{onSkip && (
						<div className="mt-4 mb-2 text-center">
							<button
								id="onboarding_email_otp_skip"
								type="button"
								onClick={handleSkip}
								className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
							>
								{OTP_COPY.skip}
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

export default StepOtp;
